import { signalStore, withState, withMethods, patchState } from '@ngrx/signals';
import { CustomGridStackWidget } from '../../services/grid-widget-config.service';
import { inject } from '@angular/core';
import { Bookmark, LayoutOptions } from '../../models/bookmark';
import { BookmarkStore } from '../bookmark/bookmark.state';

interface GridState {
  activeBookmark: Bookmark | undefined;
  bookmarkToEdit: Bookmark | undefined;
  showBookmarkForm: boolean;
  items: CustomGridStackWidget[];
  layout: CustomGridStackWidget[];
  options: LayoutOptions | null;
  overwriteLayout: boolean;
  layoutChanged: boolean;
  initialized: boolean;
  readonly: null | boolean;
  gridHasChanges: boolean;
}

const initialGridState: GridState = {
  activeBookmark: undefined,
  bookmarkToEdit: undefined,
  showBookmarkForm: false,
  items: [],
  layout: [],
  options: null,
  overwriteLayout: false,
  layoutChanged: false,
  initialized: false,
  readonly: null,
  gridHasChanges: false,
};

export const GridStore = signalStore(
  { providedIn: 'root' },
  withState(initialGridState),
  withMethods((store, bookmarkStore = inject(BookmarkStore)) => ({
    reset() {
      patchState(store, initialGridState);
    },
    setInitialized(initialized: boolean) {
      patchState(store, { initialized });
    },
    setActiveBookmarkById(bookmarkId: string, forceBookmark?: Bookmark): void {
      const bookmarkToActivate =
        forceBookmark || bookmarkStore.getByIdOrToken(bookmarkId);

      if (bookmarkToActivate && bookmarkToActivate.config) {
        patchState(store, {
          activeBookmark: bookmarkToActivate,
          layout: structuredClone(bookmarkToActivate.config),
          options: bookmarkToActivate.layoutOptions,
          overwriteLayout: true,
          gridHasChanges: false,
        });
        setTimeout(() =>
          patchState(store, { overwriteLayout: false, gridHasChanges: false })
        );
      } else {
        patchState(store, {
          activeBookmark: undefined,
          layout: [],
          options: null,
          overwriteLayout: true,
          gridHasChanges: false,
        });
        setTimeout(() =>
          patchState(store, { overwriteLayout: false, gridHasChanges: false })
        );
      }
    },
    unsetActiveBookmark(): void {
      patchState(store, {
        activeBookmark: undefined,
        readonly: null,
        layout: [],
      });
    },
    saveLayout(layout: any): void {
      patchState(store, { layout, layoutChanged: false });
    },
    resetGridHasChanges(): void {
      console.log('resetGridHasChanges');
      patchState(store, { gridHasChanges: false });
    },
    loadLayout(layout: CustomGridStackWidget[], options: LayoutOptions) {
      patchState(store, { layout, overwriteLayout: true, options });
      setTimeout(() => patchState(store, { overwriteLayout: false }));
    },
    markFeature(
      featureKey: string,
      type: 'feature' | 'indicator' | 'dataLayer' = 'feature'
    ): void {
      const options: LayoutOptions = store.options() || {
        selectedFeatures: [],
        highlightedFeature: undefined,
        activeTopic: null,
      };

      const currentFeatures = options.selectedFeatures || [];
      const existingIndex = currentFeatures.findIndex(
        item => item.key === featureKey && item.type === type
      );

      if (existingIndex !== -1) {
        // Remove if already selected
        currentFeatures.splice(existingIndex, 1);
      } else {
        // Add new selection
        currentFeatures.push({ key: featureKey, type });
      }

      options.selectedFeatures = currentFeatures;
      patchState(store, { options });
    },
    highlightFeature(featureKey?: string, type?: string): void {
      const options: LayoutOptions = store.options() || {
        selectedFeatures: [],
        highlightedFeature: undefined,
        activeTopic: null,
      };

      queueMicrotask(() => {
        options.highlightedFeature =
          featureKey && type ? { key: featureKey, type: type } : undefined;
        patchState(store, { options });
      });
    },
    layout(): CustomGridStackWidget[] {
      return store.layout();
    },
    openBookmarkForm(bookmark?: Bookmark): void {
      patchState(store, { showBookmarkForm: true, bookmarkToEdit: bookmark });
    },
    closeBookmarkForm() {
      patchState(store, { showBookmarkForm: false, bookmarkToEdit: undefined });
    },
    triggerLayoutChanges(): void {
      if (store.initialized()) {
        patchState(store, { layoutChanged: true, gridHasChanges: true });
      }
    },
    setReadonly(state: boolean): void {
      patchState(store, { readonly: state });
    },
  }))
);
